import React, { useRef, useState } from "react";
import { Power, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Menu, Share2, LayoutGrid, Sparkles, Info, Undo2, Volume1, Volume2, Play, Pause } from "lucide-react";
const buttonBase =
  "relative rounded-full bg-[#0F0F0F] shadow-[3px_3px_8px_rgba(0,0,0,0.85),-1px_-1px_3px_rgba(255,255,255,0.07)] active:shadow-[inset_3px_3px_8px_rgba(0,0,0,0.95)] flex flex-col items-center justify-center text-neutral-300 select-none cursor-pointer transition-all active:translate-y-[1px] disabled:opacity-40 disabled:cursor-not-allowed";

// Press-and-hold helper. Fires `onLongPress` once the pointer has been
// down for >= 550ms; otherwise `onClick` fires on release. Used by the
// POWER + SELECT buttons for their hidden secondary actions.
function useLongPress({ onClick, onLongPress, ms = 550 }) {
  const timerRef = useRef(null);
  const longFiredRef = useRef(false);
  const start = () => {
    longFiredRef.current = false;
    if (!onLongPress) return;
    timerRef.current = setTimeout(() => {
      longFiredRef.current = true;
      onLongPress();
    }, ms);
  };
  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const end = () => {
    cancel();
    if (!longFiredRef.current && onClick) onClick();
  };
  return {
    onPointerDown: start,
    onPointerUp: end,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
  };
}

function PadButton({ onClick, children, label, testId, disabled, className = "", handlers, attention = false }) {
  // If `handlers` (from useLongPress) is supplied, those pointer events drive
  // both short-press and long-press; we omit the native `onClick` so it
  // doesn't double-fire alongside `onPointerUp`.
  const pointerProps = handlers || {};
  return (
    <button
      onClick={handlers ? undefined : onClick}
      {...pointerProps}
      disabled={disabled}
      data-testid={testId}
      className={`${buttonBase} ${attention ? "text-[var(--phosphor)] ring-2 ring-[var(--phosphor)] shadow-[0_0_18px_color-mix(in_srgb,var(--phosphor)_70%,transparent),inset_0_0_10px_color-mix(in_srgb,var(--phosphor)_18%,transparent)] animate-pulse" : ""} ${className}`}
    >
      {children}
      {label && (
        <span
          className={`text-[9px] mt-[2px] tracking-[0.2em] font-bold ${attention ? "text-[var(--phosphor)] phosphor-glow" : "text-neutral-500"}`}
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {label}
        </span>
      )}
    </button>
  );
}

export default function Remote({
  power,
  isOwner,
  onPower,
  onPowerLongPress,
  onChannelUp,
  onChannelDown,
  onMenu,
  onShare,
  onGuide,
  onUpdates,
  updatesAvailable = false,
  onSelect,
  onSelectLongPress,
  onDigit,
  onLast,
  onDpadUp,
  onDpadDown,
  onDpadLeft,
  onDpadRight,
  digitBuffer = "",
  currentChannelNumber = null,
  playing = false,
  paused = false,
  volume = 0,
  onVolumeDown,
  onVolumeUp,
  onPlayPause,
  onPictureMode,
  brandText,
  callsign,
}) {
  const powerHandlers = useLongPress({ onClick: onPower, onLongPress: onPowerLongPress });
  const selectHandlers = useLongPress({ onClick: onSelect, onLongPress: onSelectLongPress });
  // "IR blink" — every real remote fires a brief red LED flash when
  // a command is sent. We catch clicks in the capture phase on the
  // outer container so every button contributes without wrapping
  // each callback individually. 180ms is enough to register as a
  // pop but short enough to look staccato for rapid presses.
  const [irBlink, setIrBlink] = useState(false);
  const irTimerRef = useRef(null);
  const handleAnyClick = () => {
    setIrBlink(true);
    if (irTimerRef.current) clearTimeout(irTimerRef.current);
    irTimerRef.current = setTimeout(() => setIrBlink(false), 180);
  };
  return (
    <div
      className="relative w-full bg-[#1A1A1A] rounded-[3rem] p-7 border border-neutral-800 shadow-[inset_-4px_-4px_10px_rgba(0,0,0,0.8),inset_4px_4px_10px_rgba(255,255,255,0.04),0_25px_50px_rgba(0,0,0,0.8)] flex flex-col gap-6"
      // Cabinet scale — pairs with the CRT wrapper in CRT.js so both
      // grow together on ultrawide monitors (or a manual PREFS pin).
      // Var is set on the outer layout container in App.js.
      style={{ maxWidth: `calc(240px * var(--cabinet-scale, 1))` }}
      data-testid="remote-control"
      onClickCapture={handleAnyClick}
    >
      {/* Brand strip + printed model/callsign — every real 90s remote
          had a tiny model code stamped somewhere on the top face. Both
          the centred brand wordmark and the callsign strip below it
          are theme-driven (Messages → Theme editor exposes both) so
          the operator can rebrand the whole cabinet without touching
          code. LED dot is absolutely-positioned right so it doesn't
          offset the centred brand text. */}
      <div className="relative flex items-center justify-center px-2">
        <span
          className="text-[10px] tracking-[0.35em] font-black text-neutral-400"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="remote-brand-text"
        >
          {brandText || "SCARTLEAD.TV"}
        </span>
        <span
          className={`absolute right-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${power ? "bg-[var(--phosphor)] shadow-[0_0_6px_var(--phosphor)]" : "bg-neutral-700"}`}
        />
      </div>
      <div className="flex justify-center -mt-4 -mb-2">
        <span
          data-testid="remote-model-strip"
          className="text-[8px] tracking-[0.35em] font-medium text-neutral-600"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          MODEL · {callsign || "CRT-1986"}
        </span>
      </div>

      {/* Power button — short-press toggles CRT power, long-press cycles
          scanline intensity (off → normal → heavy). When the TV is OFF
          the button pulses with a soft red halo so brand-new viewers
          notice the "you need to turn this on first" affordance. */}
      <div className="flex justify-center">
        <button
          {...powerHandlers}
          data-testid="power-button"
          className={`w-14 h-14 rounded-full bg-[#FF3B30] flex items-center justify-center text-white font-bold shadow-[3px_3px_8px_rgba(0,0,0,0.85),-1px_-1px_3px_rgba(255,100,100,0.4)] active:shadow-[inset_3px_3px_8px_rgba(0,0,0,0.95)] active:translate-y-[1px] transition-all ${
            !power ? "animate-power-pulse" : ""
          }`}
          aria-label="Power"
        >
          <Power size={22} strokeWidth={2.5} />
        </button>
      </div>

      {/* Channel rocker moved below the D-pad — see comment there. */}

      {/* Navigation cluster — classic TV-remote D-pad: round black plate
          with four chevron tabs at the compass points and a raised SELECT
          puck in the centre. Reads as a single physical control instead
          of five loose buttons. SELECT keeps its short-press (re-summon
          NOW BROADCASTING) + long-press (EPG peek). Up/Down → guide nav
          when guide open, otherwise channel ↑/↓. Left/Right → previous /
          next tape on the current channel. Tiny "NAV" cap-label above the
          dish ties it to the CH ▼▲ and 0–9 labels for a coherent 90s
          hardware feel. */}
      <div className="flex flex-col items-center gap-1 w-full">
        <span
          className="text-[10px] tracking-[0.3em] font-bold text-neutral-500"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          NAV
        </span>
        <div
          className="relative w-full aspect-square rounded-full bg-[#070707] shadow-[inset_4px_4px_12px_rgba(0,0,0,0.9),inset_-2px_-2px_6px_rgba(255,255,255,0.04),3px_3px_8px_rgba(0,0,0,0.6)] flex items-center justify-center"
          data-testid="dpad-cluster"
        >
          {/* Subtle inner ring — implies the round dish shape of a real
              remote's D-pad housing. Pure cosmetic; pointer-events-none
              so it never intercepts clicks. */}
          <div
            className="absolute inset-2 rounded-full pointer-events-none border border-[#1A1A1A]/60"
          />

          {/* Each chevron is wrapped in its own absolutely-positioned
              `<div>` because `PadButton`'s shared base class includes
              `relative`, which Tailwind emits AFTER `absolute` in its
              generated CSS — making any inline `absolute` we add to the
              button itself silently lose the position fight. The wrapper
              div sidesteps that without forcing `!important` on every
              position class. */}

          {/* UP — anchored top-center */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2">
            <PadButton
              testId="dpad-up"
              onClick={onDpadUp}
              disabled={!power || !onDpadUp}
              className="w-11 h-11 !bg-[#1F1F1F] hover:!bg-[#262626]"
            >
              <ChevronUp size={22} strokeWidth={2.5} />
            </PadButton>
          </div>

          {/* LEFT — anchored mid-left */}
          <div className="absolute left-2 top-1/2 -translate-y-1/2">
            <PadButton
              testId="dpad-left"
              onClick={onDpadLeft}
              disabled={!power || !onDpadLeft}
              className="w-11 h-11 !bg-[#1F1F1F] hover:!bg-[#262626]"
            >
              <ChevronLeft size={22} strokeWidth={2.5} />
            </PadButton>
          </div>

          {/* RIGHT — anchored mid-right */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <PadButton
              testId="dpad-right"
              onClick={onDpadRight}
              disabled={!power || !onDpadRight}
              className="w-11 h-11 !bg-[#1F1F1F] hover:!bg-[#262626]"
            >
              <ChevronRight size={22} strokeWidth={2.5} />
            </PadButton>
          </div>

          {/* DOWN — anchored bottom-center */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <PadButton
              testId="dpad-down"
              onClick={onDpadDown}
              disabled={!power || !onDpadDown}
              className="w-11 h-11 !bg-[#1F1F1F] hover:!bg-[#262626]"
            >
              <ChevronDown size={22} strokeWidth={2.5} />
            </PadButton>
          </div>

          {/* SELECT — centre puck. No wrapper needed: it's the only flex
              child so the parent's `items-center justify-center` puts it
              dead-centre on its own. Slightly lighter than the chevrons
              with a phosphor-coloured border so the eye lands here first. */}
          <PadButton
            testId="select-button"
            handlers={selectHandlers}
            disabled={!power}
            className="w-14 h-14 z-10 !bg-[#2A2A2A] hover:!bg-[#333333] border-2 border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] !shadow-[4px_4px_10px_rgba(0,0,0,0.95),-1px_-1px_3px_rgba(255,255,255,0.08),inset_0_-1px_3px_rgba(0,0,0,0.5)]"
          >
            <Info size={16} strokeWidth={2.5} />
            <span
              className="text-[8px] tracking-[0.2em] font-bold mt-0.5 text-neutral-200"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              SEL
            </span>
          </PadButton>
        </div>
      </div>

      {/* Channel rocker — sits below the D-pad in a horizontal
          orientation (◄ CH ►) so the primary tune-in control lives
          just under the main navigation cluster, matching the "flick
          up/down the dial" muscle memory. Full width to match the
          0–9 keypad's outer capsule below, so the two blocks stack as
          a coherent pair. */}
      

      {/* Player controls — one interface into the existing App/CRT player. */}
      <div className="flex flex-col items-center gap-1 w-full" data-testid="remote-transport">
        <div className="flex items-center justify-between w-full px-1">
          <span
            className="text-[10px] tracking-[0.3em] font-bold text-neutral-500"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            TV CONTROL
          </span>
          <span
            className="text-[9px] tracking-[0.18em] text-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] tabular-nums"
            style={{ fontFamily: "VT323, monospace" }}
            data-testid="remote-volume-readout"
          >
            VOL {String(Math.max(0, Math.min(10, Number(volume) || 0))).padStart(2, "0")}
          </span>
        </div>

        <div className="bg-[#0F0F0F] rounded-2xl p-2 shadow-[inset_2px_2px_8px_rgba(0,0,0,0.85)] grid grid-cols-3 gap-2 w-full">
          <PadButton
            testId="channel-down"
            onClick={onChannelDown}
            disabled={!power}
            className="w-full h-12"
            label="CH −"
          >
            <ChevronDown size={18} strokeWidth={2.5} />
          </PadButton>

          <PadButton
            testId="remote-play-pause"
            onClick={onPlayPause}
            disabled={!power || !onPlayPause}
            className="w-full h-12 !bg-[#222222] border border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)]"
            label={paused ? "PLAY" : "PAUSE"}
          >
            {paused
              ? <Play size={19} strokeWidth={2.5} fill="currentColor" />
              : <Pause size={19} strokeWidth={2.5} fill="currentColor" />}
          </PadButton>

          <PadButton
            testId="channel-up"
            onClick={onChannelUp}
            disabled={!power}
            className="w-full h-12"
            label="CH +"
          >
            <ChevronUp size={18} strokeWidth={2.5} />
          </PadButton>

          <PadButton
            testId="remote-volume-down"
            onClick={onVolumeDown}
            disabled={!power || !onVolumeDown}
            className="w-full h-12"
            label="VOL −"
          >
            <Volume1 size={18} strokeWidth={2.4} />
          </PadButton>

          <PadButton
            testId="remote-picture-mode"
            onClick={onPictureMode}
            disabled={!power || !onPictureMode}
            className="w-full h-12 !bg-[#1B1B1B]"
            label="PICTURE"
          >
            <span
              className="text-[12px] leading-none font-bold"
              style={{ fontFamily: "VT323, monospace" }}
            >
              4:3
            </span>
          </PadButton>

          <PadButton
            testId="remote-volume-up"
            onClick={onVolumeUp}
            disabled={!power || !onVolumeUp}
            className="w-full h-12"
            label="VOL +"
          >
            <Volume2 size={18} strokeWidth={2.4} />
          </PadButton>
        </div>
      </div>

      {/* Digit pad — type a channel number to cut to it directly. The
          buffer is owned by App.js (shared with keyboard digits) so a
          digit pressed here picks up wherever the keyboard left off, and
          vice-versa. While the viewer is mid-type the buffer shows in a
          small slot above the pad ("CH 12_") and the CRT mirrors the same
          slate on-screen. Disabled when the cabinet is off. */}
      {onDigit && (
        <div className="flex flex-col items-center gap-1 -mt-1" data-testid="digit-pad">
          <div className="flex items-center justify-between w-full px-1">
            <span
              className="text-[10px] tracking-[0.3em] font-bold text-neutral-500"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              0 – 9
            </span>
            {/* Buffer slot doubles as a status readout — when the
                viewer isn't typing, we display the currently-tuned
                channel number (padded 2-digit, muted amber phosphor
                glow) so the strip on top of the keypad reads like a
                real remote's segmented CH display. Typing kicks it
                into full-brightness phosphor with the live buffer.
                When a tape is actively playing on a real channel a
                tiny pulsing phosphor dot precedes the number — like
                a cable-box front panel's "signal locked" LED. Dot
                disappears while paused, tuned to a virtual channel,
                or powered off. */}
            <span
              data-testid="remote-digit-buffer"
              className={`text-[11px] tabular-nums tracking-[0.25em] font-bold inline-flex items-center gap-1 ${
                digitBuffer
                  ? "text-[var(--phosphor)] phosphor-glow"
                  : currentChannelNumber
                  ? "text-[color-mix(in_srgb,var(--phosphor)_55%,transparent)]"
                  : "text-neutral-700"
              }`}
              style={{ fontFamily: "VT323, monospace", minWidth: "2.2em", justifyContent: "flex-end" }}
            >
              {!digitBuffer && currentChannelNumber && playing && (
                <span
                  data-testid="remote-signal-dot"
                  aria-label="Signal locked"
                  className="w-1.5 h-1.5 rounded-full bg-[var(--phosphor)] shadow-[0_0_4px_var(--phosphor)] animate-pulse"
                />
              )}
              {digitBuffer || (currentChannelNumber ? String(currentChannelNumber).padStart(2, "0") : "––")}
            </span>
          </div>
          <div className="bg-[#0F0F0F] rounded-2xl p-2 shadow-[inset_2px_2px_8px_rgba(0,0,0,0.85)] grid grid-cols-3 gap-[6px] w-full">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <PadButton
                key={d}
                testId={`digit-${d}`}
                onClick={() => onDigit(String(d))}
                disabled={!power}
                className="w-full h-9 text-base font-bold"
              >
                <span style={{ fontFamily: "VT323, monospace" }} className="text-lg leading-none">{d}</span>
              </PadButton>
            ))}
            {/* Bottom row: LAST (jump back to previous channel) on the
                left, 0 in the centre, UPDATES on the right — fills out
                the 3-wide grid without leaving a dead corner. */}
            {onLast ? (
              <PadButton
                testId="last-channel"
                onClick={onLast}
                disabled={!power}
                className="w-full h-9"
                label="LAST"
              >
                <Undo2 size={14} strokeWidth={2.5} />
              </PadButton>
            ) : (
              <div />
            )}
            <PadButton
              testId="digit-0"
              onClick={() => onDigit("0")}
              disabled={!power}
              className="w-full h-9 text-base font-bold"
            >
              <span style={{ fontFamily: "VT323, monospace" }} className="text-lg leading-none">0</span>
            </PadButton>
            <PadButton
              testId="updates-button"
              onClick={onUpdates}
              disabled={!power}
              attention={updatesAvailable && power}
              className="w-full h-9"
              label={updatesAvailable ? "NEW UPDATE" : "UPDATES"}
            >
              <Sparkles size={14} strokeWidth={2.5} />
            </PadButton>
          </div>
        </div>
      )}

      {/* Guide + Share row */}
      <div className="grid grid-cols-2 gap-3 mt-1">
        <PadButton
          testId="guide-button"
          onClick={onGuide}
          disabled={!power}
          label="GUIDE"
          className="w-full h-14"
        >
          <LayoutGrid size={18} strokeWidth={2.5} />
        </PadButton>

        <PadButton
          testId="remote-share-button"
          onClick={onShare}
          disabled={false}
          label="SHARE"
          className="w-full h-14"
        >
          <Share2 size={18} strokeWidth={2.5} />
        </PadButton>
      </div>

      {/* MENU button — owner-only upload terminal trigger. */}
      {isOwner && (
        <div className="flex justify-center">
          <PadButton
            testId="menu-button"
            onClick={onMenu}
            disabled={!power}
            label="MENU"
            className="w-full h-12"
          >
            <Menu size={18} strokeWidth={2.5} />
          </PadButton>
        </div>
      )}

      {/* IR LED — dark red static blob by default, blinks bright red
          + glow for ~180ms whenever any button in the remote is
          pressed. `onClickCapture` on the outer container drives the
          state so every button contributes without wrapping each
          callback individually. */}
      <div className="flex justify-center mt-1">
        <div
          data-testid="remote-ir-led"
          className={`w-3 h-3 rounded-full transition-all duration-150 ${
            irBlink
              ? "bg-[#FF3B30] shadow-[0_0_10px_rgba(255,59,48,0.9),inset_0_0_4px_rgba(255,255,255,0.4)]"
              : "bg-red-900/60 shadow-[inset_0_0_4px_rgba(255,0,0,0.7)]"
          }`}
        />
      </div>
    </div>
  );
}
